import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Save, ArrowLeft, Loader2, FileText } from "lucide-react";
import axios from "axios";
import { API_URL } from "../../../api/api";
import { toast } from "../../../utils/toast";
import {
  LETTER_TYPES,
  DEFAULT_TEMPLATES,
  getPlaceholders,
  replacePlaceholders,
} from "../../../utils/letterTemplates";
import "./letterTemplates.css";

/* ── Placeholder fields per letter type ── */
const PLACEHOLDER_FIELDS = [
  { key: "employeeName",       label: "Employee Name",       type: "text" },
  { key: "designation",        label: "Designation",         type: "text" },
  { key: "newDesignation",     label: "New Designation",     type: "text" },
  { key: "department",         label: "Department",          type: "text" },
  { key: "companyName",        label: "Company Name",        type: "text" },
  { key: "manager",            label: "Reporting Manager",   type: "text" },
  { key: "salary",             label: "Annual CTC",          type: "text" },
  { key: "monthlySalary",      label: "Monthly Salary",      type: "text" },
  { key: "newSalary",          label: "New Salary",          type: "text" },
  { key: "currentSalary",      label: "Current Salary",      type: "text" },
  { key: "incrementPercentage",label: "Increment %",         type: "number" },
  { key: "joinDate",           label: "Join Date",           type: "date" },
  { key: "effectiveDate",      label: "Effective Date",      type: "date" },
  { key: "lastWorkingDay",     label: "Last Working Day",    type: "date" },
  { key: "responseDate",       label: "Response Deadline",   type: "date" },
  { key: "confirmationDate",   label: "Confirmation Date",   type: "date" },
  { key: "issueDate",          label: "Issue Date",          type: "date" },
  { key: "offerDate",          label: "Offer Date",          type: "date" },
  { key: "incidentDate",       label: "Incident Date",       type: "date" },
  { key: "startDate",          label: "Start Date",          type: "date" },
  { key: "probationPeriod",    label: "Probation Period (months)", type: "number" },
  { key: "duration",           label: "Duration",            type: "text" },
  { key: "stipend",            label: "Stipend",             type: "text" },
  { key: "responsibilities",   label: "Responsibilities",    type: "textarea" },
  { key: "qualities",          label: "Qualities / Skills",  type: "text" },
  { key: "project",            label: "Project / Task",      type: "text" },
  { key: "description",        label: "Description",         type: "textarea" },
  { key: "issue",              label: "Issue / Reason",      type: "text" },
  { key: "location",           label: "Location",            type: "text" },
  { key: "signatoryName",      label: "Signatory Name",      type: "text" },
  { key: "signatoryTitle",     label: "Signatory Title",     type: "text" },
  { key: "employeeEmail",      label: "Employee Email",      type: "email" },
];

/* Return only the fields whose placeholder appears in the template */
const getRelevantFields = (bodyTemplate, subjectTemplate) => {
  const combined = `${subjectTemplate}${bodyTemplate}`;
  return PLACEHOLDER_FIELDS.filter(f => combined.includes(`{{${f.key}}}`));
};

const CreateTemplate = () => {
  const navigate  = useNavigate();
  const userData  = JSON.parse(localStorage.getItem("userdata") || "{}");
  const role      = localStorage.getItem("role");

  const [activeTab,        setActiveTab]        = useState("Preview");
  const [selectedType,     setSelectedType]     = useState("offer");
  const [openTypeDropdown, setOpenTypeDropdown] = useState(false);

  /* Template from DB or default */
  const [subjectTemplate, setSubjectTemplate] = useState("");
  const [bodyTemplate,    setBodyTemplate]    = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  /* Form values (filled by user) */
  const [form, setForm] = useState({});

  const [saving, setSaving] = useState(false);

  /* Load template when type changes */
  useEffect(() => {
    loadTemplateForType(selectedType);
  }, [selectedType]);

  const loadTemplateForType = async (type) => {
    setLoadingTemplate(true);
    try {
      /* Try fetching from backend first */
      const { data } = await axios.get(`${API_URL}/api/settings/letter-templates`);
      const match = Array.isArray(data) ? data.find((t) => t.document_type === type) : null;
      if (match) {
        setSubjectTemplate(match.subject || "");
        setBodyTemplate(match.body || "");
      } else {
        /* Fall back to defaults */
        const def = DEFAULT_TEMPLATES[type];
        setSubjectTemplate(def?.subject || "");
        setBodyTemplate(def?.content || "");
      }
    } catch {
      const def = DEFAULT_TEMPLATES[type];
      setSubjectTemplate(def?.subject || "");
      setBodyTemplate(def?.content || "");
    } finally {
      setLoadingTemplate(false);
      setForm({});
    }
  };

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const renderSubject = () => replacePlaceholders(subjectTemplate, form);
  const renderBody    = () => replacePlaceholders(bodyTemplate, form);

  const relevantFields = getRelevantFields(bodyTemplate, subjectTemplate);

  const handleSave = async (isFinal = false) => {
    if (!selectedType) return toast.error("Please select a letter type");
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/settings/letter-templates/generate-pdf`, {
        template_name : LETTER_TYPES.find((t) => t.value === selectedType)?.label || selectedType,
        document_type : selectedType,
        subject       : subjectTemplate,
        body          : bodyTemplate,
        placeholders  : relevantFields.map((f) => f.key),
        preview_values: form,
        created_by    : userData.id,
        is_final      : isFinal,
      });
      toast.success(isFinal ? "Letter generated & saved!" : "Saved as draft");
      navigate(`/${role}/settings/letter-templates`);
    } catch (err) {
      console.error("Error saving letter:", err);
      toast.error("Failed to save letter");
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = LETTER_TYPES.find((t) => t.value === selectedType)?.label || selectedType;

  return (
    <div className="lt-page">
      {/* Header */}
      <div className="lt-page-header">
        <div className="lt-back-header">
          <button
            className="lt-back-btn"
            onClick={() => navigate(`/${role}/settings/letter-templates`)}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h2 className="lt-page-title">Generate Letter</h2>
            <p className="lt-page-subtitle">Fill in the details to generate a letter</p>
          </div>
        </div>
        <div className="lt-header-actions">
          <button className="lt-btn-ghost" onClick={() => handleSave(false)} disabled={saving}>
            Save Draft
          </button>
          <button className="lt-btn-primary" onClick={() => handleSave(true)} disabled={saving}>
            {saving
              ? <><Loader2 size={14} className="lt-spin" /> Saving…</>
              : <><Save size={14} /> Generate &amp; Save</>}
          </button>
        </div>
      </div>

      <div className="lt-create-grid">
        {/* ── Left: Preview ── */}
        <div className="lt-create-main">
          {/* Preview tabs */}
          <div className="lt-preview-tabs">
            {["Preview", "Subject"].map((tab) => (
              <button
                key={tab}
                className={`lt-preview-tab ${activeTab === tab ? "lt-preview-tab--active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Preview heading */}
          <div className="lt-preview-heading">
            <FileText size={18} />
            <div>
              <h3>{typeLabel}</h3>
              <p>{form.employeeName || "Employee Name"} · {new Date().toLocaleDateString("en-GB")}</p>
            </div>
          </div>

          {/* Preview content */}
          {loadingTemplate ? (
            <div className="lt-loading"><Loader2 className="lt-spinner" /></div>
          ) : (
            <div className="lt-preview-card">
              {activeTab === "Subject" ? (
                <div className="lt-subject-preview">
                  <span className="lt-subject-label">Subject:</span>
                  {renderSubject() || <em className="lt-muted">No subject</em>}
                </div>
              ) : (
                <div
                  className="lt-body-preview"
                  dangerouslySetInnerHTML={{ __html: renderBody().replace(/\n/g, "<br/>") }}
                />
              )}
            </div>
          )}
        </div>

        {/* ── Right: Controls ── */}
        <div className="lt-create-sidebar">
          {/* Type selector */}
          <div className="lt-control-card">
            <div className="lt-control-label">Letter Type</div>
            <div
              className="lt-dropdown-trigger"
              onClick={() => setOpenTypeDropdown((v) => !v)}
              style={{ position: "relative" }}
            >
              <span>{typeLabel}</span>
              <span className="lt-chevron">▾</span>
              {openTypeDropdown && (
                <div className="lt-dropdown-list">
                  {LETTER_TYPES.map((t) => (
                    <div
                      key={t.value}
                      className={`lt-dropdown-item ${selectedType === t.value ? "lt-dropdown-item--active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedType(t.value);
                        setOpenTypeDropdown(false);
                      }}
                    >
                      {t.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Dynamic placeholder fields */}
          <div className="lt-control-card lt-fields-card">
            <div className="lt-control-label">Letter Details</div>
            {relevantFields.length === 0 ? (
              <p className="lt-muted">No editable fields for this template.</p>
            ) : (
              relevantFields.map((field) => (
                <div key={field.key} className="lt-field">
                  <label className="lt-label">{field.label}</label>
                  {field.type === "textarea" ? (
                    <textarea
                      className="lt-textarea lt-textarea--sm"
                      name={field.key}
                      rows={3}
                      value={form[field.key] || ""}
                      onChange={handleChange}
                      placeholder={`Enter ${field.label.toLowerCase()}…`}
                    />
                  ) : (
                    <input
                      className="lt-input"
                      type={field.type}
                      name={field.key}
                      value={form[field.key] || ""}
                      onChange={handleChange}
                      placeholder={`Enter ${field.label.toLowerCase()}…`}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Subject template (editable) */}
          <div className="lt-control-card">
            <div className="lt-control-label">Subject Template</div>
            <textarea
              className="lt-textarea lt-textarea--sm"
              rows={2}
              value={subjectTemplate}
              onChange={(e) => setSubjectTemplate(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplate;